import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { listRequestsInbox, listMyRequests } from "../data/requests";
import {
  listBookingsForUser,
  listBookingsForTutorUser,
  updateBooking,
  cancelBooking,
  rescheduleBooking,
} from "../data/bookings";
import { submitReview } from "../data/reviews";
import { submitDispute } from "../data/disputes";
import { toId } from "../lib/subjects";
import { getRequestStatusMeta } from "../lib/requestStatus";
import RescheduleModal from "./components/RescheduleModal";

const theme = {
  bg: "#272c39",
  primary: "#7269e3",
  secondary: "#a783a6",
  success: "#98dea3",
  danger: "#fca5a5",
};

function normalizeRole(role) {
  return String(role || "").trim().toUpperCase();
}

function safeTs(iso) {
  const ts = new Date(iso).getTime();
  return Number.isFinite(ts) ? ts : null;
}

function fmt(iso, lng) {
  if (!iso) return "";
  const ts = safeTs(iso);
  if (ts === null) return "";
  return new Date(ts).toLocaleString(lng, { dateStyle: "medium", timeStyle: "short" });
}

function subjectLabel(t, subject) {
  if (!subject) return t("bookings.labels.request");
  return t(`subjects.${toId(subject)}`, { defaultValue: String(subject) });
}

function counterpartyName({ isTutor, item, t }) {
  const n = isTutor ? item?.studentName : item?.tutorName;
  if (n && String(n).trim()) return String(n).trim();
  return isTutor ? t("common.student") : t("common.tutor");
}

function requestKindKey(kind) {
  return String(kind || "TRIAL").toUpperCase() === "TASK" ? "TASK" : "TRIAL";
}

function requestKindLabel(t, kind) {
  return requestKindKey(kind) === "TASK"
    ? t("bookings.requestKinds.task")
    : t("bookings.requestKinds.trial");
}

function requestUiStatus(t, status) {
  return getRequestStatusMeta(t, status);
}

function bookingUiStatus(t, status) {
  const s = String(status || "").toUpperCase();
  if (s === "PENDING") return { text: t("bookings.bookingStatuses.pending"), tone: "subtle" };
  if (s === "ACCEPTED") return { text: t("bookings.bookingStatuses.accepted"), tone: "success" };
  if (s === "DECLINED") return { text: t("bookings.bookingStatuses.declined"), tone: "danger" };
  if (s === "CANCELLED") return { text: t("bookings.bookingStatuses.cancelled"), tone: "danger" };
  if (s === "COMPLETED") return { text: t("bookings.bookingStatuses.completed"), tone: "subtle" };
  return { text: t("bookings.bookingStatuses.unknown"), tone: "subtle" };
}

function formatRateUAH(rate) {
  const n = Number(rate);
  if (!Number.isFinite(n)) return "";
  return `₴${Math.round(n)}/год`;
}

export default function Bookings() {
  const { t, i18n } = useTranslation();
  const lng = i18n.resolvedLanguage || i18n.language || "en";

  const auth = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("auth") || "null");
    } catch {
      return null;
    }
  }, []);

  const role = normalizeRole(auth?.role);
  const isAuthed = !!auth?.token;
  const isTutor = role === "TUTOR";
  const isStudent = role === "STUDENT";
  const hasSupportedRole = isTutor || isStudent;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");

  const [bookings, setBookings] = useState([]);
  const [requests, setRequests] = useState([]);

  const [tab, setTab] = useState("REQUESTS"); // REQUESTS | LESSONS | PAST
  const [reqTab, setReqTab] = useState("TRIAL"); // TRIAL | TASK

  const [busyKey, setBusyKey] = useState("");
  const [resched, setResched] = useState({ open: false, booking: null });
  const [reviewModal, setReviewModal] = useState({ open: false, booking: null });
  const [disputeModal, setDisputeModal] = useState({ open: false, booking: null });

  const refresh = useCallback(
    async ({ silent = false } = {}) => {
      if (!isAuthed) {
        setBookings([]);
        setRequests([]);
        setError("");
        setLoading(false);
        setRefreshing(false);
        return;
      }

      if (!hasSupportedRole) {
        setBookings([]);
        setRequests([]);
        setError("");
        setLoading(false);
        setRefreshing(false);
        return;
      }

      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      try {
        const [b, r] = await Promise.all([
          isTutor ? listBookingsForTutorUser() : listBookingsForUser(),
          isTutor ? listRequestsInbox() : listMyRequests(),
        ]);

        setBookings(Array.isArray(b) ? b : []);
        setRequests(Array.isArray(r) ? r : []);
      } catch {
        setBookings([]);
        setRequests([]);
        setError(t("bookings.errors.load"));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [hasSupportedRole, isAuthed, isTutor, t]
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  const confirmedRequests = useMemo(() => {
    const arr = Array.isArray(requests) ? requests : [];

    const confirmed = arr.filter(
      (r) => !!r?.contactsUnlocked || String(r?.status || "").toUpperCase() === "CHAT_ACTIVE"
    );

    const filtered = confirmed.filter((r) => requestKindKey(r?.kind) === reqTab);

    return filtered.sort((a, b) => {
      const ta = safeTs(a?.updatedAt || a?.createdAt) ?? 0;
      const tb = safeTs(b?.updatedAt || b?.createdAt) ?? 0;
      return tb - ta;
    });
  }, [requests, reqTab]);

  const trialsCount = useMemo(() => {
    const arr = Array.isArray(requests) ? requests : [];
    return arr.filter(
      (r) =>
        requestKindKey(r?.kind) === "TRIAL" &&
        (!!r?.contactsUnlocked || String(r?.status || "").toUpperCase() === "CHAT_ACTIVE")
    ).length;
  }, [requests]);

  const tasksCount = useMemo(() => {
    const arr = Array.isArray(requests) ? requests : [];
    return arr.filter(
      (r) =>
        requestKindKey(r?.kind) === "TASK" &&
        (!!r?.contactsUnlocked || String(r?.status || "").toUpperCase() === "CHAT_ACTIVE")
    ).length;
  }, [requests]);

  useEffect(() => {
    if (reqTab === "TRIAL" && trialsCount === 0 && tasksCount > 0) {
      setReqTab("TASK");
    }
    if (reqTab === "TASK" && tasksCount === 0 && trialsCount > 0) {
      setReqTab("TRIAL");
    }
  }, [reqTab, trialsCount, tasksCount]);

  const now = Date.now();

  const sortedBookings = useMemo(() => {
    const arr = Array.isArray(bookings) ? bookings : [];
    return arr
      .filter((b) => safeTs(b?.startISO) !== null)
      .sort((a, b) => (safeTs(a.startISO) ?? 0) - (safeTs(b.startISO) ?? 0));
  }, [bookings]);

  const upcomingLessons = useMemo(() => {
    return sortedBookings.filter((b) => {
      const ts = safeTs(b.startISO) ?? 0;
      const st = String(b.status || "").toLowerCase();
      if (["declined", "cancelled"].includes(st)) return false;
      return ts >= now;
    });
  }, [sortedBookings, now]);

  const pastLessons = useMemo(() => {
    return sortedBookings.filter((b) => {
      const ts = safeTs(b.startISO) ?? 0;
      const st = String(b.status || "").toLowerCase();
      if (["declined", "cancelled", "completed"].includes(st)) return true;
      return ts < now;
    });
  }, [sortedBookings, now]);

  const openReschedule = (b) => setResched({ open: true, booking: b });
  const closeReschedule = () => setResched({ open: false, booking: null });

  const runAction = useCallback(
    async (key, action) => {
      setActionError("");
      setBusyKey(key);
      try {
        await action();
        await refresh({ silent: true });
      } catch {
        setActionError(t("bookings.errors.action"));
      } finally {
        setBusyKey("");
      }
    },
    [refresh, t]
  );

  const saveReschedule = useCallback(
    async (newISO) => {
      if (!resched.booking) return;
      await runAction(`reschedule-${resched.booking.id}`, async () => {
        await rescheduleBooking(resched.booking.id, newISO);
      });
      closeReschedule();
    },
    [resched.booking, runAction]
  );

  if (!isAuthed) {
    return (
      <main className="min-h-screen" style={{ background: theme.bg, color: "white" }}>
        <div className="mx-auto max-w-5xl px-4 pt-10 pb-28 md:pb-10">
          <PageState
            title={t("bookings.authRequired.title")}
            subtitle={t("bookings.authRequired.subtitle")}
          />
        </div>
      </main>
    );
  }

  if (!hasSupportedRole) {
    return (
      <main className="min-h-screen" style={{ background: theme.bg, color: "white" }}>
        <div className="mx-auto max-w-5xl px-4 pt-10 pb-28 md:pb-10">
          <PageState
            title={t("bookings.unsupportedRole.title")}
            subtitle={t("bookings.unsupportedRole.subtitle")}
          />
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen" style={{ background: theme.bg, color: "white" }}>
      <div className="mx-auto max-w-5xl px-4 pt-10 pb-28 md:pb-10">
        <header className="mb-7 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">{t("bookings.title")}</h1>
            <p className="mt-1 text-sm text-white/55">
              {isTutor ? t("bookings.subtitleTutor") : t("bookings.subtitleStudent")}
            </p>
          </div>

          <button
            onClick={() => refresh({ silent: true })}
            disabled={refreshing}
            className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white/80 hover:bg-white/[0.07] disabled:opacity-60"
          >
            {refreshing ? t("bookings.refreshing") : t("common.refresh")}
          </button>
        </header>

        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-2">
          <div className="flex flex-wrap gap-2">
            <TabBtn active={tab === "REQUESTS"} onClick={() => setTab("REQUESTS")} color={theme.primary}>
              {t("bookings.tabs.activeRequests")}
            </TabBtn>
            <TabBtn active={tab === "LESSONS"} onClick={() => setTab("LESSONS")} color={theme.secondary}>
              {t("bookings.tabs.upcomingLessons")}
            </TabBtn>
            <TabBtn active={tab === "PAST"} onClick={() => setTab("PAST")} color={theme.secondary}>
              {t("bookings.tabs.history")}
            </TabBtn>
          </div>
        </div>

        {loading ? (
          <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-10 text-center text-white/70">
            {t("common.loading")}
          </div>
        ) : null}

        {!loading && error ? (
          <div className="mt-6 rounded-2xl border border-red-300/20 bg-red-500/10 px-4 py-4 text-sm text-red-100">
            {error}
          </div>
        ) : null}

        {!loading && actionError ? (
          <div className="mt-6 rounded-2xl border border-red-300/20 bg-red-500/10 px-4 py-4 text-sm text-red-100">
            {actionError}
          </div>
        ) : null}

        {!loading && tab === "REQUESTS" && (
          <section className="mt-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-lg font-semibold text-white/90">
                  {t("bookings.tabs.activeRequests")}
                </div>
                <div className="mt-1 text-sm text-white/55">
                  {t("bookings.activeRequestsHint")}
                </div>
              </div>

              <div className="flex gap-2">
                <Pill
                  active={reqTab === "TRIAL"}
                  onClick={() => setReqTab("TRIAL")}
                  color={theme.primary}
                  label={`${t("bookings.requestKinds.trial")} (${trialsCount})`}
                />
                <Pill
                  active={reqTab === "TASK"}
                  onClick={() => setReqTab("TASK")}
                  color={theme.secondary}
                  label={`${t("bookings.requestKinds.task")} (${tasksCount})`}
                />
              </div>
            </div>

            <div className="mt-4 space-y-3">
              {confirmedRequests.length === 0 ? (
                <Empty title={t("bookings.emptyRequests.title")} subtitle={t("bookings.emptyRequests.subtitle")} />
              ) : (
                confirmedRequests.map((r) => {
                  const subj = subjectLabel(t, r.subject);
                  const who = counterpartyName({ isTutor, item: r, t });
                  const preferred = r?.preferredAt ? fmt(r.preferredAt, lng) : "";
                  const preferredText = preferred
                    ? `${t("bookings.labels.preferredTime")}: ${preferred}`
                    : t("bookings.labels.preferredTimeNotSet");

                  const chatPeerId = isTutor ? r.studentId : r.tutorId;
                  const chatPeerName = isTutor ? r.studentName || t("common.student") : r.tutorName || t("common.tutor");
                  const requestStatus = requestUiStatus(t, r.status);

                  return (
                    <Card key={r.id} accent={reqTab === "TRIAL" ? theme.primary : theme.secondary}>
                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="truncate text-base font-semibold text-white/95">
                              {subj} <span className="text-white/55">•</span> {who}
                            </div>
                            <Chip text={requestKindLabel(t, r.kind)} />
                            <Chip text={requestStatus.text} tone={requestStatus.tone} />
                          </div>

                          <div className="mt-1 text-sm text-white/60">{preferredText}</div>

                          {r?.message ? (
                            <div className="mt-2 line-clamp-2 text-sm text-white/50">{r.message}</div>
                          ) : null}

                          <div className="mt-2 text-xs text-white/45">
                            {requestKindKey(r.kind) === "TASK"
                              ? t("bookings.nextStepTask")
                              : t("bookings.nextStepTrial")}
                          </div>
                        </div>

                        <div className="flex shrink-0 items-center gap-2">
                          <PrimaryLink
                            to={`/chat/${chatPeerId}?name=${encodeURIComponent(chatPeerName)}`}
                            color={theme.primary}
                          >
                            {requestKindKey(r.kind) === "TASK" ? t("bookings.actions.discussDetails") : t("bookings.actions.arrangeTime")}
                          </PrimaryLink>
                        </div>
                      </div>
                    </Card>
                  );
                })
              )}
            </div>
          </section>
        )}

        {!loading && tab === "LESSONS" && (
          <section className="mt-6">
            <div>
              <div className="text-lg font-semibold text-white/90">{t("bookings.upcomingTitle")}</div>
              <div className="mt-1 text-sm text-white/55">{t("bookings.upcomingHint")}</div>
            </div>

            <div className="mt-4 space-y-3">
              {upcomingLessons.length === 0 ? (
                <Empty title={t("bookings.emptyUpcoming.title")} subtitle={t("bookings.emptyUpcoming.subtitle")} />
              ) : (
                upcomingLessons.map((b) => (
                  <LessonRow
                    key={b.id}
                    b={b}
                    lng={lng}
                    isTutor={isTutor}
                    isStudent={isStudent}
                    t={t}
                    muted={false}
                    busyKey={busyKey}
                    onReschedule={() => openReschedule(b)}
                    onAccept={() =>
                      runAction(`accept-${b.id}`, async () => {
                        await updateBooking(b.id, { status: "accepted" });
                      })
                    }
                    onDecline={() =>
                      runAction(`decline-${b.id}`, async () => {
                        await updateBooking(b.id, { status: "declined" });
                      })
                    }
                    onCancel={() =>
                      runAction(`cancel-${b.id}`, async () => {
                        await cancelBooking(b.id, "student");
                      })
                    }
                    onDispute={isStudent && !b.dispute ? () => setDisputeModal({ open: true, booking: b }) : null}
                    existingDispute={b.dispute || null}
                  />
                ))
              )}
            </div>
          </section>
        )}

        {!loading && tab === "PAST" && (
          <section className="mt-6">
            <div>
              <div className="text-lg font-semibold text-white/90">{t("bookings.historyTitle")}</div>
              <div className="mt-1 text-sm text-white/55">{t("bookings.historyHint")}</div>
            </div>

            <div className="mt-4 space-y-3">
              {pastLessons.length === 0 ? (
                <Empty title={t("bookings.emptyPast.title")} subtitle={t("bookings.emptyPast.subtitle")} />
              ) : (
                pastLessons.map((b) => (
                  <LessonRow
                    key={b.id}
                    b={b}
                    lng={lng}
                    isTutor={isTutor}
                    isStudent={isStudent}
                    t={t}
                    muted
                    busyKey={busyKey}
                    onReschedule={null}
                    onAccept={null}
                    onDecline={null}
                    onCancel={null}
                    onRate={isStudent && !b.review ? () => setReviewModal({ open: true, booking: b }) : null}
                    existingReview={b.review || null}
                    onDispute={isStudent && !b.dispute ? () => setDisputeModal({ open: true, booking: b }) : null}
                    existingDispute={b.dispute || null}
                  />
                ))
              )}
            </div>
          </section>
        )}
      </div>

      <RescheduleModal
        open={resched.open}
        initialISO={resched.booking?.startISO}
        onClose={closeReschedule}
        onSave={saveReschedule}
      />

      <ReviewModal
        open={reviewModal.open}
        booking={reviewModal.booking}
        t={t}
        onClose={() => setReviewModal({ open: false, booking: null })}
        onSubmitted={() => {
          setReviewModal({ open: false, booking: null });
          refresh({ silent: true });
        }}
      />

      <DisputeModal
        open={disputeModal.open}
        booking={disputeModal.booking}
        t={t}
        onClose={() => setDisputeModal({ open: false, booking: null })}
        onSubmitted={() => {
          setDisputeModal({ open: false, booking: null });
          refresh({ silent: true });
        }}
      />
    </main>
  );
}

/* ---------- Components ---------- */

function Card({ children, accent }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4 transition hover:bg-white/[0.08]">
      <div className="flex gap-3">
        <div className="w-1.5 rounded-full" style={{ background: accent, opacity: 0.9 }} />
        <div className="flex-1">{children}</div>
      </div>
    </div>
  );
}

function Empty({ title, subtitle }) {
  return (
    <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] p-10 text-center">
      <div className="font-medium text-white/75">{title}</div>
      {subtitle ? <div className="mt-1 text-sm text-white/50">{subtitle}</div> : null}
    </div>
  );
}

function PageState({ title, subtitle }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-10 text-center">
      <div className="text-xl font-semibold text-white/90">{title}</div>
      {subtitle ? <div className="mt-2 text-sm text-white/55">{subtitle}</div> : null}
    </div>
  );
}

function TabBtn({ active, onClick, children, color }) {
  return (
    <button
      onClick={onClick}
      className="rounded-xl border px-4 py-2 text-sm font-semibold transition"
      style={{
        borderColor: active ? `${color}66` : "rgba(255,255,255,0.10)",
        background: active ? `${color}22` : "rgba(255,255,255,0.04)",
        color: "rgba(255,255,255,0.90)",
      }}
    >
      {children}
    </button>
  );
}

function Pill({ active, onClick, label, color }) {
  return (
    <button
      onClick={onClick}
      className="rounded-full border px-3 py-1.5 text-xs font-semibold transition"
      style={{
        borderColor: active ? `${color}66` : "rgba(255,255,255,0.12)",
        background: active ? `${color}22` : "rgba(255,255,255,0.04)",
        color: "rgba(255,255,255,0.88)",
      }}
    >
      {label}
    </button>
  );
}

function Chip({ text, tone = "subtle" }) {
  let styles = {
    border: "rgba(255,255,255,0.12)",
    bg: "rgba(255,255,255,0.04)",
    color: "rgba(255,255,255,0.72)",
  };

  if (tone === "success") {
    styles = {
      border: `${theme.success}55`,
      bg: `${theme.success}22`,
      color: "rgba(255,255,255,0.92)",
    };
  }

  if (tone === "danger") {
    styles = {
      border: `${theme.danger}55`,
      bg: `${theme.danger}18`,
      color: "rgba(255,255,255,0.92)",
    };
  }

  return (
    <span
      className="inline-flex items-center rounded-full border px-2.5 py-1 text-xs"
      style={{
        borderColor: styles.border,
        background: styles.bg,
        color: styles.color,
      }}
    >
      {text}
    </span>
  );
}

function PrimaryLink({ to, children, color }) {
  return (
    <Link
      to={to}
      className="rounded-xl border px-3 py-2 text-sm font-semibold transition"
      style={{
        borderColor: `${color}66`,
        background: `${color}22`,
        color: "rgba(255,255,255,0.95)",
      }}
    >
      {children}
    </Link>
  );
}

function GhostBtn({ onClick, children, color, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="rounded-xl border px-3 py-2 text-sm transition disabled:opacity-60"
      style={{
        borderColor: "rgba(255,255,255,0.12)",
        background: "transparent",
        color: color || "rgba(255,255,255,0.80)",
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.background = "rgba(255,255,255,0.06)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      {children}
    </button>
  );
}

function LessonRow({
  b,
  lng,
  isTutor,
  isStudent,
  t,
  onReschedule,
  onAccept,
  onDecline,
  onCancel,
  onRate,
  existingReview,
  onDispute,
  existingDispute,
  muted,
  busyKey,
}) {
  const subj = subjectLabel(t, b.subject);
  const who = counterpartyName({ isTutor, item: b, t });
  const when = fmt(b.startISO, lng);

  const modeKey = b.mode === "in-person" ? "inPerson" : b.mode;
  const modeLabel = t(`modes.${modeKey}`, { defaultValue: b.mode || "" });

  const needsPayment = String(b.status || "").toUpperCase() === "ACCEPTED" && String(b.paymentStatus || "").toUpperCase() !== "PAID";
  const statusUi = bookingUiStatus(t, b.status);

  const chatPeerId = isTutor ? b.studentId : b.tutorId;
  const chatPeerName = isTutor ? b.studentName || t("common.student") : b.tutorName || t("common.tutor");

  const isAcceptBusy = busyKey === `accept-${b.id}`;
  const isDeclineBusy = busyKey === `decline-${b.id}`;
  const isCancelBusy = busyKey === `cancel-${b.id}`;
  const isRescheduleBusy = busyKey === `reschedule-${b.id}`;
  const anyBusy = isAcceptBusy || isDeclineBusy || isCancelBusy || isRescheduleBusy;

  return (
    <div
      className={`rounded-2xl border px-4 py-3 transition ${
        muted ? "border-white/5 bg-white/[0.03]" : "border-white/10 bg-white/[0.06] hover:bg-white/[0.08]"
      }`}
    >
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <div className="text-base font-semibold text-white/95 truncate">
            {subj} <span className="text-white/55">•</span> {who}
          </div>

          <div className="mt-1 text-sm text-white/60">
            {when} • {modeLabel}
            {b?.rate ? <span>{` • ${formatRateUAH(b.rate)}`}</span> : null}
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Chip text={statusUi.text} tone={statusUi.tone} />
            {needsPayment ? <Chip text={t("bookings.paymentPending")} tone="success" /> : null}
          </div>

          {needsPayment ? (
            <div className="mt-2 text-xs text-white/45">{t("bookings.paymentPendingHint")}</div>
          ) : null}
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          <PrimaryLink to={`/chat/${chatPeerId}?name=${encodeURIComponent(chatPeerName)}`} color={theme.primary}>
            {t("actions.openChat")}
          </PrimaryLink>

          {isStudent && !muted && onReschedule ? (
            <GhostBtn onClick={onReschedule} color={theme.secondary} disabled={anyBusy}>
              {isRescheduleBusy ? t("common.saving") : t("bookings.actions.reschedule")}
            </GhostBtn>
          ) : null}

          {isStudent && !muted && onCancel ? (
            <GhostBtn onClick={onCancel} color={theme.secondary} disabled={anyBusy}>
              {isCancelBusy ? t("common.updating") : t("bookings.actions.cancel")}
            </GhostBtn>
          ) : null}

          {isStudent && muted && onRate ? (
            <GhostBtn onClick={onRate} color="#f7cf52" disabled={false}>
              ★ {t("bookings.actions.rateLesson")}
            </GhostBtn>
          ) : null}

          {isStudent && muted && existingReview ? (
            <div className="flex items-center gap-1 text-xs text-white/60">
              {"★".repeat(existingReview.stars)}{"☆".repeat(5 - existingReview.stars)}
              <span className="ml-1">{t("profile.reviewEdit")}</span>
            </div>
          ) : null}

          {isStudent && onDispute ? (
            <GhostBtn onClick={onDispute} color="rgba(252,165,165,0.9)" disabled={false}>
              ⚑ {t("bookings.actions.reportProblem")}
            </GhostBtn>
          ) : null}

          {isStudent && existingDispute ? (
            <span className="rounded-full border border-red-300/30 bg-red-500/10 px-2.5 py-1 text-xs text-red-200">
              ⚑ {t("bookings.dispute.flaggedLabel")}
            </span>
          ) : null}

          {isTutor && !muted && String(b.status || "").toUpperCase() === "PENDING" ? (
            <>
              <button
                onClick={onAccept}
                disabled={anyBusy}
                className="rounded-xl border px-3 py-2 text-sm font-semibold transition disabled:opacity-60"
                style={{
                  borderColor: `${theme.primary}66`,
                  background: `${theme.primary}22`,
                  color: "rgba(255,255,255,0.95)",
                }}
              >
                {isAcceptBusy ? t("common.updating") : t("bookings.actions.accept")}
              </button>

              <GhostBtn onClick={onDecline} color={theme.secondary} disabled={anyBusy}>
                {isDeclineBusy ? t("common.updating") : t("bookings.actions.decline")}
              </GhostBtn>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function StarPicker({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className="text-2xl transition hover:scale-110"
          style={{ color: n <= value ? "#f7cf52" : "rgba(255,255,255,0.25)" }}
          aria-label={`${n} star${n !== 1 ? "s" : ""}`}
        >
          ★
        </button>
      ))}
    </div>
  );
}

function ReviewModal({ open, booking, t, onClose, onSubmitted }) {
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) { setStars(0); setComment(""); setError(""); setDone(false); }
  }, [open]);

  if (!open || !booking) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (stars === 0) return;
    setSubmitting(true);
    setError("");
    try {
      await submitReview({ bookingId: booking.id, stars, comment });
      setDone(true);
      setTimeout(onSubmitted, 1200);
    } catch (err) {
      setError(err?.data?.error === "ALREADY_REVIEWED"
        ? t("profile.reviewErrorAlready")
        : t("profile.reviewErrorGeneral"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.65)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-white/10 p-6"
        style={{ background: "#272c39" }}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">{t("profile.reviewLeave")}</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white text-xl leading-none">×</button>
        </div>

        <div className="mb-3 text-sm text-white/60">
          {booking.subject} · {booking.tutorName}
        </div>

        {done ? (
          <div className="py-6 text-center text-[#98dea3] font-semibold">{t("profile.reviewSuccess")}</div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <div className="mb-2 text-sm text-white/80">{t("profile.reviewStarsLabel")}</div>
              <StarPicker value={stars} onChange={setStars} />
            </div>

            <div>
              <div className="mb-1 text-sm text-white/80">{t("profile.reviewCommentLabel")}</div>
              <textarea
                rows={3}
                maxLength={1000}
                placeholder={t("profile.reviewCommentPlaceholder")}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="w-full resize-y rounded-xl border border-white/10 bg-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7269e3]"
              />
            </div>

            {error ? (
              <div className="rounded-xl border border-red-300/20 bg-red-500/10 p-3 text-sm text-red-100">{error}</div>
            ) : null}

            <button
              type="submit"
              disabled={submitting || stars === 0}
              className="w-full rounded-xl bg-[#7269e3] px-4 py-2 font-semibold text-white hover:brightness-110 disabled:opacity-50"
            >
              {submitting ? t("profile.reviewSubmitting") : t("profile.reviewSubmit")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const DISPUTE_REASONS = ["GHOSTED", "NO_CONTACT", "QUALITY", "PAYMENT", "OTHER"];

function DisputeModal({ open, booking, t, onClose, onSubmitted }) {
  const [reason, setReason] = useState(DISPUTE_REASONS[0]);
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) { setReason(DISPUTE_REASONS[0]); setDescription(""); setError(""); setDone(false); }
  }, [open]);

  if (!open || !booking) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await submitDispute(booking.id, reason, description);
      setDone(true);
      setTimeout(onSubmitted, 1800);
    } catch (err) {
      setError(err?.data?.error === "ALREADY_DISPUTED"
        ? t("bookings.dispute.errorAlready")
        : t("bookings.dispute.errorGeneral"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.65)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-white/10 p-6"
        style={{ background: "#272c39" }}
      >
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">{t("bookings.dispute.modalTitle")}</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white text-xl leading-none">×</button>
        </div>
        <p className="mb-4 text-sm text-white/55">{t("bookings.dispute.subtitle")}</p>

        <div className="mb-4 text-sm text-white/60">
          {booking.subject} · {booking.tutorName}
        </div>

        {done ? (
          <div className="py-6 text-center space-y-1">
            <div className="text-[#98dea3] font-semibold text-base">{t("bookings.dispute.successTitle")}</div>
            <div className="text-sm text-white/60">{t("bookings.dispute.successBody")}</div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <div className="mb-1 text-sm text-white/80">{t("bookings.dispute.reasonLabel")}</div>
              <div className="space-y-2">
                {DISPUTE_REASONS.map((r) => (
                  <label key={r} className="flex cursor-pointer items-center gap-3 rounded-xl border border-white/10 px-3 py-2 text-sm hover:bg-white/5">
                    <input
                      type="radio"
                      name="reason"
                      value={r}
                      checked={reason === r}
                      onChange={() => setReason(r)}
                      className="accent-[#7269e3]"
                    />
                    <span className="text-white/85">{t(`bookings.dispute.reasons.${r}`)}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <div className="mb-1 text-sm text-white/80">{t("bookings.dispute.descriptionLabel")}</div>
              <textarea
                rows={3}
                maxLength={2000}
                placeholder={t("bookings.dispute.descriptionPlaceholder")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full resize-y rounded-xl border border-white/10 bg-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7269e3]"
              />
            </div>

            {error ? (
              <div className="rounded-xl border border-red-300/20 bg-red-500/10 p-3 text-sm text-red-100">{error}</div>
            ) : null}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-xl bg-red-500/80 px-4 py-2 font-semibold text-white hover:bg-red-500 disabled:opacity-50"
            >
              {submitting ? t("bookings.dispute.submitting") : t("bookings.dispute.submit")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
